import { api } from './api';

export const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    // The API returns the data directly at the top level, not nested in 'data'
    return response;
  };
  
  export const register = async (userData) => {
    const response = await api.post('/auth/register', userData);
    return response;
  };
  
  export const logout = async () => {
    const response = await api.post('/auth/logout');
    return response;
  };
  
  export const refreshToken = async (refreshToken) => {
    try {
      const response = await api.post('/auth/refresh-token', { refreshToken });
      return response || {};
    } catch (error) {
      console.error('Error refreshing token:', error);
      throw error; // Re-throw to be handled by the calling function
    }
  };
  
  export const forgotPassword = async (email) => {
    try {
      const response = await api.post('/auth/forgot-password', { email });
      return response;
    } catch (error) {
      console.error('Forgot password API error:', error);
      throw error;
    }
  };
  
  // Use a cache to prevent duplicate verification calls for the same token
const tokenVerificationCache = new Map();

export const verifyResetToken = async (token) => {
    // console.log('verifyResetToken called with token:', token);
    
    // Check if we already verified this token
    if (tokenVerificationCache.has(token)) {
      // console.log('Using cached token verification result');
      return tokenVerificationCache.get(token);
    }
    
    try {
      // Try GET first (original method)
      try {
        // console.log('Attempting GET verification');
        const response = await api.get(`/auth/verify-reset-token?token=${token}`);
        // console.log('GET verification successful:', response);
        
        // Normalize the response format
        const result = {
          status: 'success',
          isValid: true,
          data: response
        };
        
        // Cache the successful result
        tokenVerificationCache.set(token, result);
        return result;
      } catch (getError) {
        // console.log('GET token verification failed, trying POST fallback:', getError);
        // Fallback to POST if GET fails
        const response = await api.post(`/auth/verify-reset-token`, { token });
        // console.log('POST verification successful:', response);
        
        // Normalize the response format
        const result = {
          status: 'success',
          isValid: true,
          data: response
        };
        
        // Cache the successful result
        tokenVerificationCache.set(token, result);
        return result;
      }
    } catch (error) {
      console.error('Verify reset token API error:', error);
      // Cache failed result too
      const errorResult = { status: 'error', isValid: false, error: error.message };
      tokenVerificationCache.set(token, errorResult);
      throw error;
    }
  };
  
  export const resetPassword = async (token, password, passwordConfirm) => {
    try {
      const response = await api.patch('/auth/reset-password', {
        token,
        password,
        passwordConfirm
      });
      return response;
    } catch (error) {
      console.error('Reset password API error:', error);
      throw error;
    }
  };
  
  export const updatePassword = async (currentPassword, password, passwordConfirm) => {
    const response = await api.patch('/auth/update-password', {
      currentPassword,
      password,
      passwordConfirm
    });
    return response;
  };
  
  export const updateProfile = async (userData) => {
    const response = await api.patch('/auth/me', userData);
    
    // Handle different possible response structures
    // Check if response has the expected structure
    if (response?.status === 'success' && response?.data?.user) {
      return response.data.user;
    }
    
    // Fallback to check if user is at top level
    if (response?.user) {
      return response.user;
    }
    
    // If the response itself is the user object
    if (response && response._id && response.email) {
      return response;
    }
    
    // Log unexpected response structure for debugging
    console.error('Unexpected updateProfile response structure:', response);
    throw new Error('Invalid response structure from profile update');
  };
  
  export const getProfile = async () => {
    const response = await api.get('/auth/me');
    
    // Handle different possible response structures
    // Check if response has the expected structure
    if (response?.status === 'success' && response?.data?.user) {
      return response.data.user;
    }
    
    // Fallback to check if user is at top level
    if (response?.user) {
      return response.user;
    }
    
    // If the response itself is the user object
    if (response && response._id && response.email) {
      return response;
    }
    
    // Log unexpected response structure for debugging
    console.error('Unexpected getProfile response structure:', response);
    throw new Error('Invalid response structure from get profile');
  };